import { motion } from "framer-motion";
import { Phone } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL } from "@/lib/campaign";
import { track } from "@/lib/track";
import { MaskedLine } from "@/components/campaign/Reveal";
import { HeroQuote } from "@/components/campaign/Reviews";

const TRUST_ITEMS = [
  "60-minute first visit",
  "Board-certified physician (DO)",
  "Private pay, superbill provided",
  "HSA/FSA may apply",
];

const ConsultImage = () => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.8, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
    className="relative"
  >
    <div className="absolute -inset-2 border border-[var(--brand-gold)] opacity-50" aria-hidden="true" />
    <img
      data-testid="fm-hero-photo"
      src="/images/fm/consult-room.webp"
      alt="A physician and patient in an unhurried consult-room conversation."
      className="relative w-full h-64 sm:h-80 lg:h-[420px] object-cover shadow-lg"
    />
  </motion.div>
);

export const FmHero = ({ variant, onRequest }) => (
  <section data-testid="fm-hero-section" className="relative overflow-hidden">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-10 pb-14 sm:pt-16 sm:pb-20 grid lg:grid-cols-[1.05fr_1fr] gap-10 lg:gap-14 items-center">
      <div className="text-left">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          data-testid="fm-hero-eyebrow"
          className="text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] text-[var(--brand-gold)] mb-4"
        >
          Functional Medicine &bull; Easton, PA
        </motion.p>
        <h1
          data-testid="fm-hero-headline"
          className="font-serif-brand text-4xl sm:text-5xl lg:text-[2.9rem] font-semibold tracking-tight leading-[1.12] text-[var(--brand-navy)]"
        >
          <MaskedLine delay={0.1}>Tired of being told</MaskedLine>
          <MaskedLine delay={0.25}>
            your labs look <span className="italic text-[var(--brand-gold)]">normal?</span>
          </MaskedLine>
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.45 }}
          data-testid="fm-hero-subheadline"
          className="mt-6 text-lg leading-relaxed text-[var(--brand-navy-deep)] max-w-xl"
        >
          Spend a full hour with Dr. Jill Crosson, DO, reviewing your history, your symptoms, and
          the results you already have, so you leave with a clear starting plan.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-7"
        >
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <button
              data-testid="fm-hero-request-button"
              onClick={onRequest}
              className="inline-flex items-center justify-center bg-[var(--brand-navy)] text-white text-lg font-semibold px-10 py-4 hover:bg-[var(--brand-navy-deep)] transition-colors"
            >
              Request an Appointment
            </button>
            <a
              data-testid="fm-hero-call-link"
              href={PHONE_TEL} data-call-location="hero"
              onClick={() => track(variant, "call_click")}
              className="inline-flex items-center justify-center gap-2 border-2 border-[var(--brand-navy)] text-[var(--brand-navy)] text-lg font-semibold px-8 py-[14px] bg-white hover:bg-[var(--brand-cream)] transition-colors"
            >
              <Phone className="h-5 w-5" aria-hidden="true" /> Call {PHONE_DISPLAY}
            </a>
          </div>
          <ul
            data-testid="fm-hero-trust-row"
            className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm text-[var(--brand-navy-deep)]"
          >
            {TRUST_ITEMS.map((t, i) => (
              <li key={t} className="flex items-center gap-3">
                {i > 0 && <span className="text-[var(--brand-gold)]" aria-hidden="true">&bull;</span>}
                <span className="font-medium">{t}</span>
              </li>
            ))}
          </ul>
        </motion.div>
        <div className="hidden lg:block">
          <HeroQuote />
        </div>
      </div>
      <div className="pb-8">
        <ConsultImage />
        <div className="lg:hidden mt-12">
          <HeroQuote />
        </div>
      </div>
    </div>
    <div className="heartbeat-divider max-w-6xl mx-auto" aria-hidden="true" />
  </section>
);
